// Quotation Types and Interfaces

export enum QuotationStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  CONVERTED = 'CONVERTED',
  /** ยกเลิกโดยผู้ใช้ (มติ PO 29 ก.ย. 2026) — append ท้ายเท่านั้น */
  CANCELLED = 'CANCELLED',
  /** ถูกแทนที่ด้วยใบใหม่ (สร้างสำเนาแบบแทนที่) */
  SUPERSEDED = 'SUPERSEDED'
}

/** ประเภทรายการต่อแถว — ค่าตรง backend QuotationItemType */
export type QuotationItemTypeCode = 'RENTAL' | 'DEPOSIT' | 'SERVICE' | 'PENALTY' | 'DISCOUNT' | 'OTHER';

/** สัญญาเช่าที่เกิดจากใบเสนอราคา (ผ่านใบงานเตรียมเครื่อง · 1 เครื่อง = 1 สัญญา จึงมีได้หลายใบ) */
export interface QuotationRentalRef {
  rental_id: number;
  rental_number: string;
  rental_status: string;
  product_names: string[];
}

export interface QuotationItem {
  id?: number;
  quotation_id?: number;
  product_id?: number | null;
  site_id?: number | null;
  description: string;
  quantity: number | string;
  unit: string;
  unit_price: number | string;
  amount: number | string;
  item_type?: QuotationItemTypeCode | string;
  /** ป้ายไทยจาก backend เช่น "ค่าเช่า" / "มัดจำ" */
  item_type_label?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  rental_period_days?: number | null;
  notes?: string | null;
  sort_order?: number;
  product_name?: string | null;
  site_name?: string | null;
  created_at?: string;
}

export interface Quotation {
  id: number;
  quotation_number: string;
  company_id: number;
  company_name?: string | null;
  company_name_snapshot?: string | null;
  /** โครงการที่เสนอราคาให้ (ระดับหัวใบ) — null = ยังไม่ระบุ */
  site_id?: number | null;
  site_name?: string | null;
  quotation_date: string;
  valid_until: string;
  job_name?: string | null;
  salesperson_id?: number | null;
  salesperson_name?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  subtotal: number | string;
  /** ส่วนลดหัวใบเท่านั้น (round-trip เข้าฟอร์มตรง ๆ) */
  discount_amount: number | string;
  /** Σ แถว DISCOUNT */
  line_discount_total?: number | string;
  /** discount_amount + line_discount_total — ยอดที่หักจริง */
  discount_total?: number | string;
  discount_reason?: string | null;
  vat_rate: number | string;
  vat_amount: number | string;
  total_amount: number | string;
  withholding_tax_rate: number | string;
  withholding_tax_amount: number | string;
  net_total: number | string;
  status: QuotationStatus;
  sent_at?: string | null;        // ส่งครั้งแรก (immutable — BUG-S165-QUOSENTAT)
  last_sent_at?: string | null;   // ส่งล่าสุด (ขยับทุกครั้งที่ส่งซ้ำ)
  sent_via?: string | null;
  sent_to_email?: string | null;
  terms_conditions?: string | null;
  notes?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  rejected_reason?: string | null;
  expired_at?: string | null;
  converted_at?: string | null;
  /** legacy (ทางแปลงตรงปิดแล้ว 29 ก.ย. 2026) — ห้ามแสดงเป็น "#id" · ใช้ `rentals[]` แทน */
  converted_rental_id?: number | null;
  converted_rental_number?: string | null;
  /** ยกเลิก */
  cancelled_at?: string | null;
  cancelled_reason?: string | null;
  /** ถูกแทนที่ด้วยใบใหม่ */
  superseded_at?: string | null;
  replaces_quotation_id?: number | null;
  replaces_quotation_number?: string | null;
  replaced_by_quotation_id?: number | null;
  replaced_by_quotation_number?: string | null;
  /** สัญญาเช่าที่ได้จากใบนี้ (ทุกใบ) */
  rentals?: QuotationRentalRef[];
  /** Σ แถว DEPOSIT (ติ๊ก "เป็นมัดจำ" + มัดจำแบบเดิม) — ข้อมูลประกอบ อยู่ในยอดรวมแล้ว · หน้าดูใบแสดง "มัดจำที่เสนอ (รวมในยอดแล้ว)" (มัดจำจริงคีย์เองที่สัญญา) */
  deposit_offered_total?: number | string;
  /**
   * FB-2026-0013: ยอดที่ไม่ได้คิด VAT ใน "ยอดที่บันทึก" = total − (subtotal − ส่วนลดรวม + VAT)
   * ใบเก่าที่มีแถวมัดจำ (สูตรเดิม) > 0 · ใบใหม่ = 0 เสมอ — จอสรุปยอดแสดงบรรทัดนี้ให้ตัวเลขลงตัว
   */
  non_vat_total?: number | string;
  /** Σ ค่าบริการครั้งเดียว (SERVICE/PENALTY/OTHER) — ไม่ยกไปใบงาน */
  one_time_total?: number | string;
  warnings?: string[];
  pdf_path?: string | null;
  /**
   * ใบงานเตรียมเครื่องที่ **ยังมีชีวิต** (ไม่ถูกยกเลิก/ลบ) ล่าสุดที่เปิดจากใบเสนอราคานี้
   * — backend ส่งมาทั้ง list และ detail · null = ยังไม่มี → กด "สั่งเตรียมเครื่อง" ได้
   */
  preparation_job_id?: number | null;
  preparation_job_number?: string | null;
  /** REQUESTED / IN_PROGRESS / READY / DELIVERED */
  preparation_job_status?: string | null;
  preparation_job_status_label?: string | null;
  /**
   * FB-2026-0018: ใบแจ้งหนี้รายการเดียว (มัดจำ · ONE_OFF) ที่ออกจากใบนี้ (หรือใบเดิมที่ใบนี้ออกมาแทน) ที่ไม่ถูกยกเลิก
   * — backend ส่งมาทั้ง list/detail/export · null = ยังไม่ออก → เมนู "สร้างใบแจ้งหนี้" (สถานะ SENT/ACCEPTED/CONVERTED)
   */
  invoice_id?: number | null;
  invoice_number?: string | null;
  invoice_status?: string | null;
  items?: QuotationItem[];
  created_at: string;
  /** ชื่อผู้สร้างใบ (การ์ดข้อมูลการสร้าง หน้ารายละเอียด 28 ต.ค. 2026) */
  created_by_name?: string | null;
  updated_at?: string | null;
}

export interface QuotationForm {
  company_id: number;
  /** โครงการ (หัวใบ) — null = ไม่ระบุ · ส่ง key เสมอ */
  site_id?: number | null;
  quotation_date: string;
  valid_until: string;
  job_name?: string;
  /** null = ล้างค่า — ส่ง key เสมอ (ไม่ตัด key เงียบ) */
  salesperson_id?: number | null;
  salesperson_name?: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  discount_amount?: number;
  discount_reason?: string;
  vat_rate?: number;
  withholding_tax_rate?: number;
  terms_conditions?: string;
  notes?: string;
  items: QuotationItemForm[];
}

export interface QuotationItemForm {
  product_id?: number | null;
  site_id?: number | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount?: number;
  /** default 'RENTAL' */
  item_type?: QuotationItemTypeCode;
  period_start?: string | null;
  period_end?: string | null;
  rental_period_days?: number | null;
  notes?: string | null;
}

/** filter ชุดเดียวกันทั้ง list / summary / export (กติกา export-filter-parity) */
export interface GetQuotationsParams {
  page?: number;
  limit?: number;
  search?: string;
  company_id?: number;
  site_id?: number;
  salesperson_id?: number;
  /** เฉพาะใบที่ฉันเป็นผู้เสนอราคา */
  mine?: boolean;
  status?: QuotationStatus;
  from_date?: string;
  to_date?: string;
  valid_until_from?: string;
  valid_until_to?: string;
  /** ใกล้หมดอายุภายใน N วัน */
  expiring_within_days?: number;
  has_preparation_job?: boolean;
  rental_id?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export type GetQuotationSummaryParams = Omit<GetQuotationsParams, 'page' | 'limit' | 'sort' | 'order'>;

export interface QuotationSummary {
  total: number;
  /** ชื่อ backend `total_amount` = ยอดเฉพาะ DRAFT/SENT/ACCEPTED (pipeline) */
  total_amount: number;
  /** alias ของ total_amount ที่สื่อความหมาย (pipeline) */
  pipeline_amount?: number;
  /** ยอดใบที่แปลงเป็นสัญญาแล้ว */
  converted_amount?: number;
  draft: number;
  sent: number;
  accepted: number;
  rejected: number;
  expired: number;
  converted: number;
  cancelled?: number;
  superseded?: number;
}

export interface QuotationCancelRequest {
  reason: string;
}

export interface QuotationExtendRequest {
  /** YYYY-MM-DD */
  valid_until: string;
}

export interface QuotationDuplicateRequest {
  /** true = ออกใบใหม่แทนใบเดิม (ใบเดิม → SUPERSEDED) · false/ไม่ส่ง = สำเนาธรรมดา */
  supersede?: boolean;
}

/** ตัวเลือกสินค้าสำหรับฟอร์มใบเสนอราคา — GET /quotations/products (gate สิทธิ์ใบเสนอราคา ไม่ผูก products.view) */
export interface QuotationProductOption {
  id: number;
  name: string;
  code: string | null;
  unit: string | null;
  /** ราคาเช่า/เดือนตั้งต้นของสินค้า — ใช้เติมราคาต่อหน่วยเมื่อช่องยังว่าง */
  monthly_rental_rate: number | null;
}

export interface QuotationResponse {
  items: Quotation[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// สั่งเตรียมเครื่องจากใบเสนอราคา (feedback amintra 29 ก.ย. 2026)
// GET /quotations/{id}/preparation-job-prefill · POST /quotations/{id}/preparation-job
// ---------------------------------------------------------------------------

/** 1 แถวจากใบเสนอราคาที่ backend จับคู่สินค้าให้แล้ว (อาจจับไม่ได้ → product_id null ผู้ใช้ต้องเลือกเอง) */
export interface QuotationPreparationPrefillItem {
  quotation_item_id: number;
  description: string;
  /** integer — ใบงานนับของเป็นชิ้น: backend ปัดจำนวนทศนิยมในใบเสนอราคา **ขึ้น** เป็นจำนวนเต็ม (2.5 → 3) */
  quantity: number;
  /**
   * จำนวนตามใบเสนอราคา (ก่อนปัด · ทศนิยมได้) — ต่างจาก `quantity` = จอต้องบอกผู้ใช้ว่าถูกปรับ (E2E รอบ 2 SB-13)
   * response จาก server รุ่นเก่าไม่มี field นี้
   */
  quantity_in_quotation?: number | null;
  unit_price: number;
  product_id: number | null;
  product_name: string | null;
  /** เดาจากอะไร — null = เดาไม่ได้ */
  matched_by: 'product_id' | 'name' | null;
}

export interface QuotationPreparationPrefill {
  quotation_id: number;
  quotation_number: string;
  status: QuotationStatus | string;
  /** false = สร้างไม่ได้ (ดู blocked_reason) */
  can_create: boolean;
  /** ข้อความไทยพร้อมแสดง */
  blocked_reason: string | null;
  /** ใบงานที่ยังมีชีวิต (ไม่ถูกยกเลิก/ลบ) ที่ผูกกับใบเสนอราคานี้ */
  existing_job: { id: number; job_number: string; status: string; status_label: string } | null;
  company_id: number;
  company_name: string | null;
  /** เมื่อทุกรายการชี้โครงการเดียวกัน */
  site_id: number | null;
  site_name: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  /** backend ประกอบให้แล้ว เช่น "ใบเสนอราคา QT-… · งาน: …" */
  notes: string | null;
  /** YYYY-MM-DD จาก period_start ที่เร็วสุด หรือ null */
  needed_date: string | null;
  items: QuotationPreparationPrefillItem[];
  /** จำนวนแถวที่ product_id เป็น null */
  unmapped_count: number;
  /** แถวที่ไม่ยกไปใบงาน (มัดจำ/ค่าบริการ/ส่วนลด …) พร้อมเหตุผลไทย */
  skipped_items: Array<{
    quotation_item_id: number;
    description: string;
    item_type: string;
    item_type_label: string;
    amount: number;
    reason: string;
  }>;
  deposit_offered_total: number;
  one_time_total: number;
}

export interface QuotationPreparationJobItemInput {
  product_id: number;
  quantity: number;
  monthly_price?: number | null;
  notes?: string | null;
}

export interface QuotationPreparationJobRequest {
  site_id?: number | null;
  /** ต้องมีอย่างใดอย่างหนึ่ง (site_id หรือ site_name_text) */
  site_name_text?: string | null;
  /** บังคับ YYYY-MM-DD */
  needed_date: string;
  priority?: 'NORMAL' | 'HIGH' | 'URGENT' | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  /** ≥1 · product_id บังคับทุกแถว */
  items: QuotationPreparationJobItemInput[];
}


// ---------------------------------------------------------------------------
// FB-2026-0018: สร้างใบแจ้งหนี้ (มัดจำ) จากใบเสนอราคา
// ---------------------------------------------------------------------------

export interface QuotationInvoiceLine {
  quotation_item_id: number;
  item_type: string;
  item_type_label: string;
  description: string;
  quantity: number;
  unit?: string | null;
  unit_price: number;
  amount: number;
  /** backend ตัดสิน — FE ห้ามเดาซ้ำ */
  selectable: boolean;
  default_selected: boolean;
  warning?: string | null;
  reason?: string | null;
}

export interface QuotationInvoiceTotals {
  selected_item_ids: number[];
  subtotal: number | string;
  vat_rate: number | string;
  vat_amount: number | string;
  total_amount: number | string;
  withholding_tax_rate: number | string;
  withholding_tax_amount: number | string;
  net_total: number | string;
}

export interface QuotationInvoicePrefill {
  quotation_id: number;
  quotation_number: string;
  status: string;
  status_label?: string | null;
  can_create: boolean;
  blocked_reason?: string | null;
  existing_invoice?: {
    id: number;
    invoice_number: string;
    status: string;
    source_quotation_id?: number | null;
    source_quotation_number?: string | null;
  } | null;
  company_id: number;
  company_name?: string | null;
  invoice_date: string;
  due_date: string;
  vat_rate: number | string;
  vat_zero_reason?: string | null;
  withholding_tax_rate: number | string;
  lines: QuotationInvoiceLine[];
  totals: QuotationInvoiceTotals;
}

export interface QuotationInvoiceCreateRequest {
  quotation_item_ids: number[];
  invoice_date?: string;
  due_date?: string;
  withholding_tax_rate?: number;
  notes?: string;
}
